import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import SubscriptionSummaryCards from './components/SubscriptionSummaryCards';
import SubscriptionStatusBadge from './components/SubscriptionStatusBadge';
import CompanyListTable, { CompanyNameCell } from './components/CompanyListTable';
import useAdminSubscriptions from './hooks/useAdminSubscriptions';
import { formatDate } from '../../../utils/Utils';

const selectSx = {
  height: '40px',
  borderRadius: '8px',
  fontSize: '0.875rem',
  minWidth: 190,
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
};

/**
 * Formata uma data 'Y-m-d H:i:s' (fuso do servidor) usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

/**
 * Formata um dia 'YYYY-MM-DD' (next_due_date) como dd/MM/yyyy, sem passar por Date:
 * new Date('YYYY-MM-DD') lê o dia como meia-noite UTC e mostra a véspera às 21:00.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDay(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = String(dateStr).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
}

/**
 * Formata só dia/mês (dd/MM), para colunas mais compactas das tabelas de empresas.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDateShort(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/**
 * Formata amount_cents em Real (R$).
 * @param {number|null} cents
 * @returns {string}
 */
function fmtCurrency(cents) {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * AdminSubscriptions - Visão consolidada de assinaturas, trials e problemas de pagamento
 * (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminSubscriptions() para os cards e as quatro seções (overview.php)
 * - Renderiza AreaTitle + controles (incluir teste / janela do trial) + cards de KPI +
 *   tabelas de empresas, com o nome de cada empresa linkado para os detalhes dela
 */
function AdminSubscriptions() {
  const {
    includeTest,
    setIncludeTest,
    daysAhead,
    setDaysAhead,
    summary,
    trialExpiring,
    trialExpired,
    paymentIssues,
    canceling,
    isLoading,
    error,
    refresh,
  } = useAdminSubscriptions();

  const trialExpiringColumns = [
    { key: 'name', label: 'Empresa', render: (r) => <CompanyNameCell name={r.name} companyId={r.company_id} /> },
    { key: 'email', label: 'E-mail', render: (r) => <Typography variant="body2" color="text.secondary">{r.email || '—'}</Typography> },
    {
      key: 'trial_ends_at',
      label: 'Vence em',
      render: (r) => (
        <Box>
          <Typography variant="body2">{fmtDateShort(r.trial_ends_at)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            em {r.days_left} dia{r.days_left === 1 ? '' : 's'}
          </Typography>
        </Box>
      ),
    },
    { key: 'last_login_at', label: 'Último acesso', render: (r) => <Typography variant="body2">{fmtDate(r.last_login_at)}</Typography> },
    { key: 'users_count', label: 'Usuários', align: 'center', render: (r) => <Typography variant="body2">{r.users_count ?? 0}</Typography> },
  ];

  const trialExpiredColumns = [
    { key: 'name', label: 'Empresa', render: (r) => <CompanyNameCell name={r.name} companyId={r.company_id} /> },
    { key: 'email', label: 'E-mail', render: (r) => <Typography variant="body2" color="text.secondary">{r.email || '—'}</Typography> },
    {
      key: 'trial_ends_at',
      label: 'Venceu em',
      render: (r) => (
        <Box>
          <Typography variant="body2">{fmtDateShort(r.trial_ends_at)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            há {r.days_since} dia{r.days_since === 1 ? '' : 's'}
          </Typography>
        </Box>
      ),
    },
    { key: 'last_login_at', label: 'Último acesso', render: (r) => <Typography variant="body2">{fmtDate(r.last_login_at)}</Typography> },
    { key: 'users_count', label: 'Usuários', align: 'center', render: (r) => <Typography variant="body2">{r.users_count ?? 0}</Typography> },
  ];

  const paymentIssuesColumns = [
    { key: 'name', label: 'Empresa', render: (r) => <CompanyNameCell name={r.name} companyId={r.company_id} /> },
    { key: 'status', label: 'Status', render: (r) => <SubscriptionStatusBadge status={r.subscription_status} /> },
    { key: 'plan_name', label: 'Plano', render: (r) => <Typography variant="body2">{r.plan_name || '—'}</Typography> },
    { key: 'next_due_date', label: 'Próximo vencimento', render: (r) => <Typography variant="body2">{fmtDay(r.next_due_date)}</Typography> },
    {
      key: 'last_failed_at',
      label: 'Última falha',
      render: (r) => (
        <Box>
          <Typography variant="body2">{fmtDateShort(r.last_failed_at)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {fmtCurrency(r.last_failed_amount_cents)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'invoice',
      label: 'Fatura',
      render: (r) => (r.last_failed_invoice_url ? (
        <Link href={r.last_failed_invoice_url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
          fatura
        </Link>
      ) : (
        <Typography variant="body2" color="text.disabled">—</Typography>
      )),
    },
  ];

  const cancelingColumns = [
    { key: 'name', label: 'Empresa', render: (r) => <CompanyNameCell name={r.name} companyId={r.company_id} /> },
    { key: 'plan_name', label: 'Plano', render: (r) => <Typography variant="body2">{r.plan_name || '—'}</Typography> },
    { key: 'next_due_date', label: 'Encerra em', render: (r) => <Typography variant="body2">{fmtDay(r.next_due_date)}</Typography> },
  ];

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Assinaturas"
          Icon={<CreditCardOutlinedIcon />}
          Description="Visão consolidada de assinaturas, trials e problemas de pagamento da plataforma."
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} size="small" />}
            label={<Typography variant="body2">Incluir empresas de teste</Typography>}
          />

          <Select size="small" value={daysAhead} onChange={(e) => setDaysAhead(Number(e.target.value))} sx={selectSx}>
            <MenuItem value={7}>Trial vence em 7 dias</MenuItem>
            <MenuItem value={15}>Trial vence em 15 dias</MenuItem>
            <MenuItem value={30}>Trial vence em 30 dias</MenuItem>
          </Select>
        </Box>

        <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={refresh} disabled={isLoading}>
          Atualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <SubscriptionSummaryCards summary={summary} daysAhead={daysAhead} />

          <CompanyListTable title="Trials vencendo" count={trialExpiring.length} rows={trialExpiring} columns={trialExpiringColumns} />
          <CompanyListTable title="Trials vencidos sem assinatura" count={trialExpired.length} rows={trialExpired} columns={trialExpiredColumns} />
          <CompanyListTable title="Problemas de pagamento" count={paymentIssues.length} rows={paymentIssues} columns={paymentIssuesColumns} />
          <CompanyListTable title="Cancelando" count={canceling.length} rows={canceling} columns={cancelingColumns} />
        </>
      )}
    </Box>
  );
}

export default AdminSubscriptions;
